// Renderer-agnostic helpers for the streamed-SOG pools (splatPool.js,
// splatPoolExact.js). Under WebGL the pools keep their raw texSubImage2D
// path (private __webglTexture handles — see their comments); under
// WebGPURenderer those handles don't exist, so row writes go through
// GPUQueue.writeTexture on the GPUTexture three created for the DataTexture.

export const isWebGPU = (gl) => !!gl?.isWebGPURenderer;

export function maxTextureSize(gl) {
  if (isWebGPU(gl)) return gl.backend?.device?.limits?.maxTextureDimension2D || 8192;
  const context = gl.getContext();
  return context.getParameter(context.MAX_TEXTURE_SIZE);
}

// SH degree the pool's SH texture can actually hold: rows per splat (12 for
// degree 3) × height must fit the max texture size, AND the texture
// (RGBA16F, 8 bytes/texel) must stay under SH_TEXTURE_MAX_BYTES. Without the
// byte cap, an 18M-splat model (Cinevilla Labour Camp) sized a 16384×12360
// texture — 1.6 GB, within the size limit but more than the GPU would
// allocate — and the whole scene rendered grey/black-and-white (measured
// 2026-10-26, Chrome + Safari on an M3 Pro Mac). Measured on that Mac:
// 0.70 GB (Cinevilla SS) worked, 0.86 GB already went grey — so the cap sits
// just above the size known to work. 0 = no SH.
export const SH_TEXTURE_MAX_BYTES = 704 * 1024 * 1024;
export function fitShDegree(width, height, shDegree, maxTex) {
  const rowsFor = (d) => (d === 1 ? 3 : d === 2 ? 9 : d >= 3 ? 12 : 0);
  while (shDegree > 0 && (height * rowsFor(shDegree) > maxTex ||
    width * height * rowsFor(shDegree) * 8 > SH_TEXTURE_MAX_BYTES)) shDegree--;
  return shDegree;
}

// The pools' covAndColor texture is RGBA32UI. WebGL needs internalFormat set
// explicitly; WebGPURenderer passes internalFormat through VERBATIM as the
// GPU format ('RGBA32UI' is not one — texture creation fails), and derives
// 'rgba32uint' from RGBAIntegerFormat + UnsignedIntType by itself.
export function setUintTextureFormat(gl, texture) {
  if (!isWebGPU(gl)) texture.internalFormat = 'RGBA32UI';
}

function gpuTextureFor(gl, texture) {
  let entry = gl.backend.get(texture);
  if (!entry || !entry.texture) {
    // Creates the GPUTexture (and uploads the DataTexture's initial data)
    // synchronously on an initialized WebGPURenderer.
    gl.initTexture(texture);
    entry = gl.backend.get(texture);
  }
  return entry.texture;
}

// Writes `width` texels at (x, y) from `data` (a typed array holding exactly
// those texels, tightly packed). writeTexture has no 256-byte bytesPerRow
// rule (that's copyBufferToTexture only), so arbitrary row spans are fine.
export function writeTextureRowWebGPU(gl, texture, x, y, width, data, bytesPerTexel) {
  gl.backend.device.queue.writeTexture(
    { texture: gpuTextureFor(gl, texture), origin: { x, y } },
    data,
    { offset: 0, bytesPerRow: width * bytesPerTexel },
    { width, height: 1 }
  );
}

export const BYTES_PER_TEXEL = {
  rgba32: 16, // RGBA32F / RGBA32UI
  rgba16: 8, // RGBA16F (SH)
};

// Zeroes `count` consecutive texels from linear slot `offset` (wrapping at
// `width`) — used to make freed pool slots fully transparent, which the GPU
// sort skips (splatGpuSort.js). Only needed on WebGPU: on WebGL the CPU sort
// worker simply excludes freed slots.
let _zeros = null;
export function zeroRangeWebGPU(gl, texture, width, offset, count, bytesPerTexel) {
  const need = width * bytesPerTexel;
  if (!_zeros || _zeros.byteLength < need) _zeros = new Uint8Array(need);
  let pos = offset, remaining = count;
  while (remaining > 0) {
    const x = pos % width;
    const y = Math.floor(pos / width);
    const span = Math.min(width - x, remaining);
    writeTextureRowWebGPU(gl, texture, x, y, span, _zeros.subarray(0, span * bytesPerTexel), bytesPerTexel);
    pos += span; remaining -= span;
  }
}

// Creates the pool's GPU textures now (uploading their zero-filled initial
// data once) so the CPU-side backing arrays can be dropped immediately.
export function initPoolTexturesWebGPU(gl, textures) {
  for (const t of textures) if (t) gpuTextureFor(gl, t);
}

